/**
 * Drawing Hit-Testing & Screen-Space Proximity Geometry.
 *
 * Evaluates whether a cursor pixel position (px, py) lands on or near
 * any drawn chart shape (trendlines, rects, fibs, rays, channels, text).
 */

import { pointToSegmentDistance } from "./geometry";

export const FIB_LEVELS = [1.0, 0.764, 0.618, 0.5, 0.382, 0.214, 0, -0.618, -1.618] as const;

export interface Point2D {
  x: number;
  y: number;
}

export interface HitTestResolvers {
  coordToPixel: (time: any, price: number) => Point2D | null;
  priceToCoord: (price: number) => number | null;
  canvasWidth?: number;
  fibLevels?: readonly number[] | number[];
  getFibExtendMode?: (d: any) => string;
  ensureFibP3?: (d: any) => any;
  timeToXWithExtrapolation?: (time: any) => number | null;
  measureTextWidth?: (text: string, fontSize: number) => number;
  /**
   * Anchored VWAP only. The curve is computed from the candles, so the page supplies its polyline (screen-space
   * points) around pointer x. Without this an avwap is simply not hittable.
   */
  avwapPolyline?: (d: any, px: number) => Point2D[] | null;
}

/**
 * Finds the topmost/nearest drawing at screen coordinate (px, py) for the active symbol.
 */
export function findDrawingAtPixel(
  drawings: any[],
  currentSymbol: string,
  px: number,
  py: number,
  resolvers: HitTestResolvers,
): any | null {
  if (!Array.isArray(drawings) || drawings.length === 0) return null;

  const {
    coordToPixel,
    priceToCoord,
    canvasWidth = 2000,
    fibLevels = FIB_LEVELS,
    getFibExtendMode,
    ensureFibP3,
    timeToXWithExtrapolation,
    measureTextWidth,
    avwapPolyline,
  } = resolvers;

  // 1. Horizontal lines (checked first for snappy horizontal level selection)
  const hline = drawings.find(d => {
    if (d.symbol !== currentSymbol || d.type !== "hline") return false;
    const y = priceToCoord(d.price);
    if (y === null) return false;
    return Math.abs(py - y) <= 6;
  });
  if (hline) return hline;

  // 2. Geometric shape drawings
  for (const d of drawings) {
    if (d.symbol !== currentSymbol) continue;

    if (d.type === "trendline" && d.point1 && d.point2) {
      const p1 = coordToPixel(d.point1.time, d.point1.price);
      const p2 = coordToPixel(d.point2.time, d.point2.price);
      if (p1 && p2 && pointToSegmentDistance(px, py, p1.x, p1.y, p2.x, p2.y) <= 10) {
        return d;
      }
    }

    if (d.type === "rect" && d.point1 && d.point2) {
      const p1 = coordToPixel(d.point1.time, d.point1.price);
      const p2 = coordToPixel(d.point2.time, d.point2.price);
      if (p1 && p2) {
        const minX = Math.min(p1.x, p2.x);
        const maxX = Math.max(p1.x, p2.x);
        const minY = Math.min(p1.y, p2.y);
        const maxY = Math.max(p1.y, p2.y);

        // Inside rect
        if (px >= minX && px <= maxX && py >= minY && py <= maxY) return d;

        // Near border (within 10px)
        const nearX =
          Math.min(Math.abs(px - p1.x), Math.abs(px - p2.x)) <= 10 &&
          py >= minY - 10 &&
          py <= maxY + 10;
        const nearY =
          Math.min(Math.abs(py - p1.y), Math.abs(py - p2.y)) <= 10 &&
          px >= minX - 10 &&
          px <= maxX + 10;
        if (nearX || nearY) return d;
      }
    }

    if (d.type === "fib" && d.point1 && d.point2) {
      const fp1 = coordToPixel(d.point1.time, d.point1.price);
      const fp2 = coordToPixel(d.point2.time, d.point2.price);
      if (fp1 && fp2) {
        const fExtendMode = getFibExtendMode ? getFibExtendMode(d) : d.extend;
        let fLeftX = Math.min(fp1.x, fp2.x);
        let fRightX = Math.max(fp1.x, fp2.x);

        if (fExtendMode === "custom" && ensureFibP3 && timeToXWithExtrapolation) {
          const p3 = d.p3 || ensureFibP3(d);
          if (p3) {
            const extX = timeToXWithExtrapolation(p3.time);
            if (extX !== null && extX > fRightX) fRightX = extX;
          }
        }

        if (px >= fLeftX - 10 && px <= fRightX + 10) {
          const y0 = priceToCoord(d.point1.price);
          const y1 = priceToCoord(d.point2.price);
          if (y0 !== null && y1 !== null) {
            const pixelRange = y1 - y0;
            for (const ratio of fibLevels) {
              const y = y0 + pixelRange * ratio;
              if (Math.abs(py - y) <= 8) return d;
            }
          }
        }
      }
    }

    if (d.type === "extended_line" && d.p1 && d.p2) {
      const p1 = coordToPixel(d.p1.time, d.p1.price);
      const p2 = coordToPixel(d.p2.time, d.p2.price);
      if (p1 && p2) {
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const norm = Math.hypot(dx, dy);
        if (norm > 0) {
          const dist = Math.abs((p2.x - p1.x) * (p1.y - py) - (p1.x - px) * (p2.y - p1.y)) / norm;
          if (dist <= 10) return d;
        }
      }
    }

    if (d.type === "hray" && d.p1) {
      const p1 = coordToPixel(d.p1.time, d.p1.price);
      if (p1 && px >= p1.x - 10 && Math.abs(py - p1.y) <= 10) {
        return d;
      }
    }

    if (d.type === "ray" && d.p1 && d.p2) {
      const p1 = coordToPixel(d.p1.time, d.p1.price);
      const p2 = coordToPixel(d.p2.time, d.p2.price);
      if (p1 && p2 && pointToSegmentDistance(px, py, p1.x, p1.y, p2.x, p2.y) <= 10) {
        return d;
      }
    }

    if (d.type === "arrow" && d.p1 && d.p2) {
      const p1 = coordToPixel(d.p1.time, d.p1.price);
      const p2 = coordToPixel(d.p2.time, d.p2.price);
      if (p1 && p2 && pointToSegmentDistance(px, py, p1.x, p1.y, p2.x, p2.y) <= 10) {
        return d;
      }
    }

    if (d.type === "parallel_channel" && d.p1 && d.p2 && d.p3) {
      const p1 = coordToPixel(d.p1.time, d.p1.price);
      const p2 = coordToPixel(d.p2.time, d.p2.price);
      const p3 = coordToPixel(d.p3.time, d.p3.price);
      if (p1 && p2 && p3) {
        const mainDx = p2.x - p1.x;
        const mainDy = p2.y - p1.y;
        const w = canvasWidth;
        let mainX1 = -1000;
        let mainY1 = p1.y;
        let mainX2 = w + 1000;
        let mainY2 = p1.y;

        if (Math.abs(mainDx) > 0.001) {
          const slope = mainDy / mainDx;
          mainY1 = p1.y + (mainX1 - p1.x) * slope;
          mainY2 = p1.y + (mainX2 - p1.x) * slope;
        } else {
          mainX1 = p1.x;
          mainX2 = p1.x;
        }

        const offX = p3.x - p1.x;
        const offY = p3.y - p1.y;
        const dist1 = pointToSegmentDistance(px, py, mainX1, mainY1, mainX2, mainY2);
        const dist2 = pointToSegmentDistance(
          px,
          py,
          mainX1 + offX,
          mainY1 + offY,
          mainX2 + offX,
          mainY2 + offY,
        );
        if (Math.min(dist1, dist2) <= 10) return d;
      }
    }

    if (d.type === "text" && d.p1) {
      const p = coordToPixel(d.p1.time, d.p1.price);
      if (p) {
        const fontSize = d.fontSize || 14;
        const padding = 6;
        const text = d.text || "";
        const tw = measureTextWidth
          ? measureTextWidth(text, fontSize) + padding * 2
          : text.length * 8 + padding * 2;
        const th = fontSize + padding * 2;
        if (px >= p.x && px <= p.x + tw && py >= p.y - th && py <= p.y) {
          return d;
        }
      }
    }

    if (d.type === "price_range" && d.p1 && d.p2) {
      const p1 = coordToPixel(d.p1.time, d.p1.price);
      const p2 = coordToPixel(d.p2.time, d.p2.price);
      if (p1 && p2) {
        const midX = (p1.x + p2.x) / 2;
        const top = Math.min(p1.y, p2.y);
        const bottom = Math.max(p1.y, p2.y);
        if (pointToSegmentDistance(px, py, midX, top, midX, bottom) <= 10) {
          return d;
        }
      }
    }

    // Anchored VWAP: the hit area is the curve itself (a band across the chart would steal every click), plus the
    // anchor point. The page hands back only the points near the pointer, so this stays O(1) per mouse move.
    if (d.type === "avwap" && avwapPolyline) {
      const pts = avwapPolyline(d, px);
      if (pts && pts.length) {
        let best = Infinity;
        if (pts.length === 1) {
          best = Math.hypot(px - pts[0].x, py - pts[0].y);
        } else {
          for (let i = 0; i < pts.length - 1; i++) {
            best = Math.min(best, pointToSegmentDistance(px, py, pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y));
          }
        }
        if (best <= 8) return d;
      }
    }

    // Long/Short Position: the whole entry/stop/target box is the hit area, with
    // the same 10px border tolerance as `rect` (p1 = entry at the left edge,
    // p2 = stop and p3 = target at the right edge).
    if (d.type === "rr_position" && d.p1 && d.p2 && d.p3) {
      const p1 = coordToPixel(d.p1.time, d.p1.price);
      const p2 = coordToPixel(d.p2.time, d.p2.price);
      const p3 = coordToPixel(d.p3.time, d.p3.price);
      if (p1 && p2 && p3) {
        const minX = Math.min(p1.x, p2.x);
        const maxX = Math.max(p1.x, p2.x);
        const minY = Math.min(p1.y, p2.y, p3.y);
        const maxY = Math.max(p1.y, p2.y, p3.y);
        if (px >= minX - 10 && px <= maxX + 10 && py >= minY - 10 && py <= maxY + 10) {
          return d;
        }
      }
    }
  }

  return null;
}
